import { Skeleton } from "@mantine/core";
import type { ComponentType } from "react";
import { useMatch } from "react-router-dom";
import { RoutePaths } from "@/core/constants/RoutePaths";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";
import { usePermission } from "@/core/auth/usePermission";
import { BillingSettingsTabSkeleton } from "@/features/billing/components/BillingSettingsTab/skeleton";
import { NotificationSoundTabSkeleton } from "@/features/notification-sound/components/NotificationSoundTab/skeleton";
import { MediaRetentionTabSkeleton } from "@/features/media-retention/components/MediaRetentionTab/skeleton";
import { AttendingTabSkeleton } from "./components/AttendingTab/skeleton";
import classes from "./skeleton.module.css";

const NAV_ITEMS = 8;

/**
 * Sem `ui:settings:view` o SettingsGate só deixa chegar às páginas pessoais
 * (Notificações para todo mundo, SA-1348; Faturamento para o admin da empresa,
 * SA-1360), e a ativa é a da rota. Com a chave, a rota padrão é Atendimentos.
 */
function resolveSkeletonContent({
  canViewSettings,
  isBillingRoute,
  isDataRetentionRoute,
  isNotificationsRoute,
}: {
  canViewSettings: boolean;
  isBillingRoute: boolean;
  isDataRetentionRoute: boolean;
  isNotificationsRoute: boolean;
}): ComponentType {
  if (isBillingRoute) return BillingSettingsTabSkeleton;
  if (isDataRetentionRoute) return MediaRetentionTabSkeleton;
  if (isNotificationsRoute || !canViewSettings)
    return NotificationSoundTabSkeleton;
  return AttendingTabSkeleton;
}

/**
 * Esqueleto do shell de Configurações (fallback de carregamento do chunk da
 * rota). Mesmo desenho da página: coluna de 252px com o título e as linhas da
 * sub-nav, e à direita o título, o banner de escopo e o esqueleto da página
 * ativa. O que depende de permissão e de conta (itens, nome) vira bloco.
 */
export function SettingsPageSkeleton() {
  const { isMobileScreen } = useMobileScreen();
  const canViewSettings = usePermission("ui:settings:view");
  const isBillingRoute =
    useMatch({ path: RoutePaths.SETTINGS_BILLING, end: false }) !== null;
  const isDataRetentionRoute =
    useMatch({ path: RoutePaths.SETTINGS_DATA_RETENTION, end: false }) !== null;
  const isNotificationsRoute =
    useMatch({ path: RoutePaths.SETTINGS_NOTIFICATIONS, end: false }) !== null;
  const ContentSkeleton = resolveSkeletonContent({
    canViewSettings,
    isBillingRoute,
    isDataRetentionRoute,
    isNotificationsRoute,
  });

  return (
    <div className={classes.shell}>
      {!isMobileScreen && (
        <div className={classes.nav}>
          <h1 className={classes.navTitle}>Configurações</h1>
          <div className={classes.navItems}>
            {Array.from({ length: NAV_ITEMS }, (_, index) => (
              <Skeleton
                key={index}
                data-testid="settings-nav-skeleton-item"
                h={36}
                radius="md"
              />
            ))}
          </div>
        </div>
      )}
      <div className={classes.content}>
        <div className={classes.inner}>
          <Skeleton
            h={36}
            w={240}
            radius="md"
          />
          <Skeleton
            h={44}
            radius="md"
          />
          <ContentSkeleton />
        </div>
      </div>
    </div>
  );
}

export default SettingsPageSkeleton;
